/**
 * The inbox bell.
 *
 * The point of the whole comms pipeline: a push you didn't catch, or an email
 * that went to spam, is still here. Every message the club has ever sent you has
 * a durable row, whatever channels you've switched off.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InboxItem } from '@gwc/shared';
import { inboxApi } from '../api/comms';

function timeAgo(iso: string): string {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function InboxBell() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const countQuery = useQuery({
    queryKey: ['inbox', 'unread-count'],
    queryFn: () => inboxApi.unreadCount(),
    // Cheap endpoint; a minute is plenty to feel live without hammering the API
    // (the live-tasting poll already spends 30 req/min of the global budget).
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const listQuery = useQuery({
    queryKey: ['inbox', 'list'],
    queryFn: () => inboxApi.list({ limit: 12 }),
    enabled: open, // don't fetch the list until someone actually opens the panel
  });

  const markRead = useMutation({
    mutationFn: (id: string) => inboxApi.markRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inbox'] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => inboxApi.markAllRead(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inbox'] });
    },
  });

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = countQuery.data ?? 0;

  function openItem(item: InboxItem) {
    if (!item.readAt) markRead.mutate(item.id);
    setOpen(false);
    if (item.link) navigate(item.link);
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread > 0 ? `Inbox — ${unread} unread` : 'Inbox'}
        aria-expanded={open}
        className="relative rounded p-2 text-cream-200 transition hover:text-gold-400"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-wine-700 px-1 text-[10px] font-semibold text-on-wine">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-ink-800 bg-ink-900 shadow-xl">
          <div className="flex items-center justify-between border-b border-ink-800 px-4 py-3">
            <span className="font-display text-sm text-cream-50">Inbox</span>
            {unread > 0 ? (
              <button
                type="button"
                className="text-xs text-gold-400 hover:text-gold-200 disabled:opacity-50"
                disabled={markAllRead.isPending}
                onClick={() => markAllRead.mutate()}
              >
                Mark all read
              </button>
            ) : null}
          </div>

          <div className="max-h-[26rem] overflow-y-auto">
            {listQuery.isLoading ? (
              <p className="px-4 py-6 text-sm text-cream-300/60">Loading…</p>
            ) : !listQuery.data?.items.length ? (
              <p className="px-4 py-8 text-center text-sm text-cream-300/50">
                Nothing yet. Club news will land here.
              </p>
            ) : (
              <ul className="divide-y divide-ink-800">
                {listQuery.data.items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => openItem(item)}
                      className={`flex w-full gap-3 px-4 py-3 text-left transition hover:bg-ink-800 ${
                        item.readAt ? '' : 'bg-ink-800/40'
                      }`}
                    >
                      <span
                        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                          item.readAt ? 'bg-transparent' : 'bg-gold-500'
                        }`}
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-cream-100">{item.title}</span>
                        <span className="mt-0.5 line-clamp-2 block text-xs text-cream-300/70">
                          {item.body}
                        </span>
                        <span className="mt-1 block text-[11px] text-cream-300/45">
                          {item.senderName ? `${item.senderName} · ` : ''}
                          {timeAgo(item.createdAt)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border-t border-ink-800 px-4 py-2 text-center">
            <Link
              to="/inbox"
              onClick={() => setOpen(false)}
              className="text-xs text-gold-400 hover:text-gold-200"
            >
              See all messages
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
